import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { fetchApiGet } from "../utils";

// ============================================================================
// Zod Schemas
// ============================================================================

export const FreightImageUploadStatus = z.enum([
  "pending_upload",
  "uploaded",
  "scanning",
  "quarantined",
  "resizing",
  "completed",
  "failed"
]);

export type FreightImageUploadStatusType = z.infer<typeof FreightImageUploadStatus>;

export const ListFreightImagesQueryParams = z.object({
  status: FreightImageUploadStatus.optional(),
  limit: z.coerce.number().min(1).max(100).default(20),
  date_from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format").optional(),
  date_to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format").optional(),
  customer_ids: z.string().optional(),
});

export type ListFreightImagesQueryParamsType = z.infer<typeof ListFreightImagesQueryParams>;

// ============================================================================
// Response Types
// ============================================================================

export interface FreightImageDocument {
  id: string;
  filename: string;
  content_type: string;
  created_at: string;
}

export interface FreightImageRelationCustomer {
  id: string;
  short_name: string;
  long_name: string;
}

export interface FreightImageUpload {
  id: string;
  created_at: string;
  status: FreightImageUploadStatusType;
  status_subtype: string | null;
  status_message: string | null;
  relation_customer: FreightImageRelationCustomer | null;
  quarantine_document: FreightImageDocument | null;
  final_document: FreightImageDocument | null;
  presigned_thumbnail_image_url: string | null;
  presigned_thumbnail_url_expires_in_seconds: number | null;
}

export interface ListFreightImagesResponse {
  uploads: FreightImageUpload[];
  pagination: {
    limit: number;
    total: number;
  };
}

export interface FreightImageUploadDetail extends FreightImageUpload {
  presigned_image_url: string | null;
  presigned_url_expires_in_seconds: number | null;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * List all freight image uploads with optional filtering
 * 
 * @param params - Query parameters for filtering and pagination
 * @param enabled - Whether the query should run automatically
 * @returns Query result with list of freight image uploads
 * 
 * @example
 * ```tsx
 * const { data, isLoading, error } = useListFreightImages({
 *   status: "completed",
 *   limit: 50,
 *   date_from: "2026-01-01",
 *   date_to: "2026-01-31"
 * });
 * ```
 */
export function useListFreightImages(
  params?: Partial<ListFreightImagesQueryParamsType>,
  enabled: boolean = true
) {
  const queryParams = new URLSearchParams();
  
  if (params?.status) {
    queryParams.append("status", params.status);
  }
  if (params?.limit !== undefined) {
    queryParams.append("limit", params.limit.toString());
  }
  if (params?.date_from) {
    queryParams.append("date_from", params.date_from);
  }
  if (params?.date_to) {
    queryParams.append("date_to", params.date_to);
  }
  if (params?.customer_ids) {
    queryParams.append("customer_ids", params.customer_ids);
  }

  return useQuery({
    queryKey: ["freight", "images", params],
    queryFn: () => fetchApiGet<ListFreightImagesResponse>(
      `/freight/images?${queryParams.toString()}`
    ),
    retry: 1,
    enabled,
  });
}

/**
 * Get details for a specific freight image upload including presigned URL
 * 
 * @param uploadId - The UUID of the freight image upload
 * @param enabled - Whether the query should run automatically
 * @returns Query result with freight image upload details
 * 
 * @example
 * ```tsx
 * const { data, isLoading } = useGetFreightImageUpload({
 *   uploadId: "550e8400-e29b-41d4-a716-446655440000"
 * });
 * 
 * // Access presigned URL
 * if (data?.success && data.data.presigned_image_url) {
 *   console.log("Image URL:", data.data.presigned_image_url);
 * }
 * ```
 */
export function useGetFreightImageUpload({
  uploadId,
  enabled = true,
}: {
  uploadId: string;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: ["freight", "images", uploadId],
    queryFn: () => fetchApiGet<FreightImageUploadDetail>(
      `/freight/images/${uploadId}`
    ),
    retry: 1,
    enabled: enabled && !!uploadId,
  });
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get display label for freight image upload status
 */
export function getFreightImageStatusLabel(status: FreightImageUploadStatusType): string {
  const labels: Record<FreightImageUploadStatusType, string> = {
    pending_upload: "Pending Upload",
    scanning: "Scanning",
    resizing: "Resizing",
    quarantined: "Quarantined",
    uploaded: "Uploaded",
    completed: "Completed",
    failed: "Failed",
  };
  return labels[status] || status;
}

/**
 * Get color class for freight image upload status badge
 */
export function getFreightImageStatusColor(status: FreightImageUploadStatusType): string {
  const colors: Record<FreightImageUploadStatusType, string> = {
    pending_upload: "bg-yellow-100 text-yellow-800",
    uploaded: "bg-blue-100 text-blue-800",
    scanning: "bg-purple-100 text-purple-800",
    resizing: "bg-purple-100 text-purple-800",
    quarantined: "bg-red-100 text-red-800",
    completed: "bg-green-100 text-green-800",
    failed: "bg-red-100 text-red-800",
  };
  return colors[status] || "bg-gray-100 text-gray-800";
}

export function isStatusFinalDownloadable(status: FreightImageUploadStatusType): boolean {
  return !["completed"].includes(status);
}
